import {
  type ApprovalDecision,
  messageRecordSchema,
  type RunTurnSseEvent,
  turnCompletedRecordSchema,
  type TurnProgressStage,
} from '@salescore/agent-contract'
import { z } from 'zod'

import { type ToolActivity, toolActivityOfMcpCall } from './toolActivity'

// runtime の通知と storage の会話記録を ChatEvent に変換する。BE が検証しない SSE の内容はここで確かめる。
// 会話記録の共通部分は agent-contract、Codex の item はこのファイルで検証する（ADR-105）。

type ToolType = 'mcpToolCall' | 'commandExecution' | 'fileChange'

export type TurnEndStatus = 'completed' | 'interrupted' | 'failed'

// 承認の要求の結末。cancelled は、利用者が答えないままターンが終わった（runtime が Codex の終了などで取り下げた）
export type ApprovalAnswer = ApprovalDecision | 'cancelled'

// 承認の要求の表示に使う値（承認のカード）。runtime が Codex の要求を取り次ぎ、ツールの呼び出しごとに1つ届く
export interface ApprovalRequest {
  // 承認の ID。回答のパスで使う
  id: string
  // 承認を待っているツールの呼び出し（ToolUpdate の id）。カードはその行の上に出す
  toolItemId: string
  approvalType: ToolType
  // コマンドの実行の承認のとき、実行しようとしているコマンド
  command: string | null
  // MCP のツールの承認のとき、カードに出す「何をしているか」の一行の種類（toolActivity.ts）。ほかの種類の承認では null
  activity: ToolActivity | null
  // MCP のツールの承認のとき、「サーバー.ツール」と、引数を読みやすく整形した JSON。カードの詳細に出す
  tool: string | null
  toolArguments: string | null
  // Codex が承認を求めた理由。なければ null
  reason: string | null
}

// ツールの呼び出しの値。同じ id の通知や記録が届いたら置き換える
export interface ToolUpdate {
  id: string
  toolType: ToolType
  // MCP は「サーバー.ツール」、コマンドは実行した文。ファイルの変更は空
  title: string
  // 出力（MCP の結果、コマンドの出力、失敗の理由）。なければ null
  detail: string | null
  status: string
  // 会話に出す「何をしているか」の一行の種類（toolActivity.ts）。会話に出さないコマンドとファイルの編集は null
  activity: ToolActivity | null
  // MCP のツールの引数を読みやすく整形した JSON。MCP でないとき、引数が届かないときは null
  toolArguments: string | null
}

export type ChatEvent =
  // 指示。会話記録から作り直すときだけ届く（送ったタブは、送った時点で表示している）
  | { kind: 'userPrompt'; text: string }
  // runtime が、考え始めるまでの段階に入った。会話記録には書かれないので、runtime の SSE だけが持つ
  | { kind: 'turnProgressed'; stage: TurnProgressStage }
  // 回答の断片。messageId は Codex の agentMessage の item の ID で、runtime の SSE だけが持つ（会話記録の断片はターンで1つの ID になる）
  | { kind: 'answerDelta'; messageId: string | null; delta: string }
  // 回答の文を書き終えた。この後にモデルが次の作業に移るときに「考え中」を出すために使う
  | { kind: 'answerFinished'; messageId: string | null }
  // 確定した回答の全文。断片で表示した回答を置き換える
  | { kind: 'answerText'; text: string }
  | { kind: 'toolUpdated'; tool: ToolUpdate }
  // ツールの実行の前に、利用者の承認を求めている（承認モードが「確認してから反映」のとき）
  | { kind: 'approvalRequested'; approval: ApprovalRequest }
  // 承認に答えた。会話記録から作り直すときだけ届く（このタブで答えたときは、API が受け付けた時点で表示を変える）
  | { kind: 'approvalAnswered'; approvalId: string; answer: ApprovalAnswer }
  // エージェントが回答を終えた（Codex の turn/completed）。runtime はこの後にタイトルの作成などをしてから completed を送る
  | { kind: 'generationCompleted' }
  | { kind: 'turnFinished'; status: TurnEndStatus; error: string | null }

const turnEndStatusSchema = z.enum(['completed', 'interrupted', 'failed'])

// storage は、知らない状態を書いた終わりの記録も書かれたまま返し、ターンの失敗として扱う（ADR-006）。
// 記録ごと捨てると、ターンの終わりも error の文言も出ないので、知らない状態は失敗として読む
const turnEndStatusOf = (status: string): TurnEndStatus => {
  const parsed = turnEndStatusSchema.safeParse(status)
  return parsed.success ? parsed.data : 'failed'
}
const approvalTypeSchema = z.enum(['commandExecution', 'fileChange', 'mcpToolCall'])

// 承認の要求の中身。runtime の SSE の approval_request の data と、会話記録の approval.requested に共通の項目
const approvalRequestFieldsSchema = z.looseObject({
  approvalId: z.string(),
  itemId: z.string(),
  command: z.string().nullish(),
  server: z.string().nullish(),
  tool: z.string().nullish(),
  arguments: z.unknown().optional(),
  reason: z.string().nullish(),
})

// 会話記録の approval.answered の decision。cancel は runtime が取り下げたもの（利用者の回答ではない）
const approvalAnswerSchema = z
  .enum(['accept', 'decline', 'cancel'])
  .transform((decision): ApprovalAnswer => (decision === 'cancel' ? 'cancelled' : decision))

// MCP のツールの結果。runtime の SSE では Codex の結果（{ content: [{ type: 'text', text }] }）がそのまま届く
const mcpResultSchema = z.object({
  content: z.array(z.looseObject({ type: z.string(), text: z.string().optional() })),
})

// Codex の item。会話記録の item は、runtime が結果と出力を要約した resultSummary、outputSummary を持ち、Codex の result は持たない
// （開始の記録には結果がなく、完了の記録には要約だけを書く）。zod 4 の unknown は項目の欠落を許さないので optional にする
const codexItemSchema = z.discriminatedUnion('type', [
  z.looseObject({ type: z.literal('agentMessage'), id: z.string() }),
  z.looseObject({
    type: z.literal('mcpToolCall'),
    id: z.string(),
    server: z.string().nullish(),
    tool: z.string().nullish(),
    status: z.string().nullish(),
    arguments: z.unknown().optional(),
    error: z.union([z.string(), z.looseObject({ message: z.string() })]).nullish(),
    result: z.unknown().optional(),
    resultSummary: z.string().nullish(),
  }),
  z.looseObject({
    type: z.literal('commandExecution'),
    id: z.string(),
    command: z.string().nullish(),
    status: z.string().nullish(),
    aggregatedOutput: z.string().nullish(),
    outputSummary: z.string().nullish(),
  }),
  z.looseObject({ type: z.literal('fileChange'), id: z.string(), status: z.string().nullish() }),
])

type CodexItem = z.infer<typeof codexItemSchema>

// Codex の通知の params。method ごとに形が違うので、method を見てから読む
const agentMessageDeltaParametersSchema = z.looseObject({ itemId: z.string(), delta: z.string() })
const itemParametersSchema = z.looseObject({ item: codexItemSchema })

// 会話記録の1行。指示と回答、ターンの終わりの形は、ターンの一覧（storage の getTurns）と同じ agent-contract のものを使い、
// 回答の断片と Codex の item のように表示にだけ使う行は、ここで読む（ADR-105）。
// ターンの終わりの status は、知らない状態の記録も捨てないよう、BE の失敗のターンと同じく文字列かだけを確かめる（ADR-006）
const transcriptEntrySchema = z.union([
  messageRecordSchema,
  z.looseObject({ type: z.literal('event'), event: z.literal('message.delta'), delta: z.string() }),
  z.looseObject({ type: z.literal('event'), event: z.literal('message.completed') }),
  z.looseObject({ type: z.literal('event'), event: z.enum(['item.started', 'item.completed']), item: codexItemSchema }),
  // 承認の種類は、レコードの type と重ならないよう approvalType にある（runtime の ADR-014）
  z.looseObject({
    type: z.literal('event'),
    event: z.literal('approval.requested'),
    approvalType: approvalTypeSchema,
    ...approvalRequestFieldsSchema.shape,
  }),
  z.looseObject({
    type: z.literal('event'),
    event: z.literal('approval.answered'),
    approvalId: z.string(),
    decision: approvalAnswerSchema,
  }),
  turnCompletedRecordSchema.extend({ status: z.string() }),
])

const mcpResultText = (result: unknown): string | null => {
  const parsed = mcpResultSchema.safeParse(result)
  const texts = parsed.success
    ? parsed.data.content.flatMap(({ type, text }) => (type === 'text' && text !== undefined ? [text] : []))
    : []
  return texts.length > 0 ? texts.join('\n') : null
}

const toolArgumentsTextOf = (toolArguments: unknown): string | null =>
  toolArguments === undefined || toolArguments === null ? null : JSON.stringify(toolArguments, null, 2)

// status を持たない開始通知は inProgress として扱う。
const toolUpdateOf = (item: CodexItem): ToolUpdate | null => {
  switch (item.type) {
    case 'agentMessage': {
      return null
    }
    case 'mcpToolCall': {
      const error = typeof item.error === 'string' ? item.error : (item.error?.message ?? null)
      return {
        id: item.id,
        toolType: 'mcpToolCall',
        title: `${item.server ?? 'mcp'}.${item.tool ?? '?'}`,
        detail: error ?? item.resultSummary ?? mcpResultText(item.result),
        status: item.status ?? 'inProgress',
        activity: toolActivityOfMcpCall(item.tool ?? null, item.arguments),
        toolArguments: toolArgumentsTextOf(item.arguments),
      }
    }
    case 'commandExecution': {
      return {
        id: item.id,
        toolType: 'commandExecution',
        title: item.command ?? '',
        detail: item.outputSummary ?? item.aggregatedOutput ?? null,
        status: item.status ?? 'inProgress',
        activity: null,
        toolArguments: null,
      }
    }
    case 'fileChange': {
      return {
        id: item.id,
        toolType: 'fileChange',
        title: '',
        detail: null,
        status: item.status ?? 'inProgress',
        activity: null,
        toolArguments: null,
      }
    }
  }
}

const approvalRequestOf = (
  approvalType: z.infer<typeof approvalTypeSchema>,
  fields: z.infer<typeof approvalRequestFieldsSchema>,
): ApprovalRequest => ({
  id: fields.approvalId,
  toolItemId: fields.itemId,
  approvalType,
  command: fields.command ?? null,
  activity: approvalType === 'mcpToolCall' ? toolActivityOfMcpCall(fields.tool ?? null, fields.arguments) : null,
  tool: approvalType === 'mcpToolCall' ? `${fields.server ?? 'mcp'}.${fields.tool ?? '?'}` : null,
  toolArguments: approvalType === 'mcpToolCall' ? toolArgumentsTextOf(fields.arguments) : null,
  reason: fields.reason ?? null,
})

const itemEventsOf = (item: CodexItem, isCompleted: boolean): ChatEvent[] => {
  if (item.type === 'agentMessage') {
    return isCompleted ? [{ kind: 'answerFinished', messageId: item.id }] : []
  }
  const tool = toolUpdateOf(item)
  return tool === null ? [] : [{ kind: 'toolUpdated', tool }]
}

const notificationEventsOf = (method: string, parameters: unknown): ChatEvent[] => {
  switch (method) {
    case 'turn/completed': {
      return [{ kind: 'generationCompleted' }]
    }
    case 'item/agentMessage/delta': {
      const parsed = agentMessageDeltaParametersSchema.safeParse(parameters)
      return parsed.success ? [{ kind: 'answerDelta', messageId: parsed.data.itemId, delta: parsed.data.delta }] : []
    }
    case 'item/started':
    case 'item/completed': {
      const parsed = itemParametersSchema.safeParse(parameters)
      return parsed.success ? itemEventsOf(parsed.data.item, method === 'item/completed') : []
    }
    default: {
      return []
    }
  }
}

/**
 * runtime の run の SSE の1イベントを、会話の表示を変える出来事にする。タイトルはチャットの一覧（#10112）で扱うので、ここでは読まない
 */
export const chatEventsOfRunEvent = (event: RunTurnSseEvent): ChatEvent[] => {
  switch (event.event) {
    case 'notification': {
      return notificationEventsOf(event.data.method, event.data.params)
    }
    case 'progress': {
      return [{ kind: 'turnProgressed', stage: event.data.stage }]
    }
    case 'completed': {
      const { finalMessage, status, error } = event.data
      const answer: ChatEvent[] = finalMessage === null ? [] : [{ kind: 'answerText', text: finalMessage }]
      return [...answer, { kind: 'turnFinished', status, error }]
    }
    case 'error': {
      return [{ kind: 'turnFinished', status: 'failed', error: event.data.message }]
    }
    case 'approval_request': {
      return [{ kind: 'approvalRequested', approval: approvalRequestOf(event.data.type, event.data) }]
    }
    case 'title': {
      return []
    }
  }
}

/**
 * storage の会話記録の1行を、会話の表示を変える出来事にする。途中からの配信（つなぎ直し）で使う
 */
export const chatEventsOfRecord = (record: unknown): ChatEvent[] => {
  const parsed = transcriptEntrySchema.safeParse(record)
  if (!parsed.success) {
    return []
  }
  const entry = parsed.data
  if (entry.type === 'message') {
    return entry.role === 'user'
      ? [{ kind: 'userPrompt', text: entry.content }]
      : [{ kind: 'answerText', text: entry.content }]
  }
  switch (entry.event) {
    case 'message.delta': {
      return [{ kind: 'answerDelta', messageId: null, delta: entry.delta }]
    }
    case 'message.completed': {
      return [{ kind: 'answerFinished', messageId: null }]
    }
    case 'item.started':
    case 'item.completed': {
      return itemEventsOf(entry.item, entry.event === 'item.completed')
    }
    case 'approval.requested': {
      return [{ kind: 'approvalRequested', approval: approvalRequestOf(entry.approvalType, entry) }]
    }
    case 'approval.answered': {
      return [{ kind: 'approvalAnswered', approvalId: entry.approvalId, answer: entry.decision }]
    }
    case 'turn.completed': {
      return [{ kind: 'turnFinished', status: turnEndStatusOf(entry.status), error: entry.error }]
    }
  }
}
